import { useState } from "react";
import { Sparkles, ArrowRight, ArrowLeft, RotateCcw, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Slider } from "@/components/ui/slider";
import { Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer } from "recharts";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const dimensions = [
  { key: "passion", label: "Passion & Enthusiasm", description: "How energized and passionate you feel about your work" },
  { key: "challenge", label: "Challenge", description: "Whether your work stretches you and keeps you engaged" },
  { key: "flexibility", label: "Flexibility", description: "Control over when, where, and how you work" },
  { key: "salary", label: "Salary & Benefits", description: "Financial compensation and benefits package" },
  { key: "progression", label: "Career Progression", description: "Opportunities for advancement and growth" },
  { key: "personalGrowth", label: "Personal Growth", description: "Development as a person, not just professionally" },
  { key: "workLifeBalance", label: "Work/Life Balance", description: "Balance between work and personal life" },
  { key: "skillDevelopment", label: "Skill Development", description: "Learning new skills and expanding capabilities" },
  { key: "meaning", label: "Meaning & Purpose", description: "Sense of contribution and meaningful impact" },
];

const CareerWheel = () => {
  const [step, setStep] = useState("access"); // access, request, intro, survey, results, application
  const [accessCode, setAccessCode] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [requestData, setRequestData] = useState({
    name: "",
    email: "",
    message: "I'd like to access the Career Wheel assessment.",
  });
  const [requestSubmitted, setRequestSubmitted] = useState(false);
  const [ratings, setRatings] = useState(
    dimensions.reduce((acc, d) => ({ ...acc, [d.key]: 50 }), {})
  );
  const [priorityAreas, setPriorityAreas] = useState("");
  const [changes, setChanges] = useState("");
  const [applicationData, setApplicationData] = useState({
    name: "",
    email: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleRatingChange = (key, value) => {
    setRatings((prev) => ({ ...prev, [key]: value[0] }));
  };

  const chartData = dimensions.map((d) => ({
    dimension: d.label,
    value: ratings[d.key],
    fullMark: 100,
  }));

  const resetSurvey = () => {
    setStep("access");
    setAccessCode("");
    setRatings(dimensions.reduce((acc, d) => ({ ...acc, [d.key]: 50 }), {}));
    setPriorityAreas("");
    setChanges("");
    setApplicationData({ name: "", email: "" });
    setSubmitted(false);
  };

  const handleVerifyAccess = async (e) => {
    e.preventDefault();
    setIsVerifying(true);
    try {
      const response = await axios.post(`${API}/career-wheel/verify-access`, {
        code: accessCode,
      });
      if (response.data.valid) {
        toast.success("Access granted!");
        setStep("intro");
      } else {
        toast.error("Invalid access code. Please try again.");
      }
    } catch (e) {
      toast.error("Verification failed. Please try again.");
    }
    setIsVerifying(false);
  };

  const handleRequestAccess = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/career-wheel/request-access`, {
        name: requestData.name,
        email: requestData.email,
        subject: "Career Wheel Access Request",
        message: requestData.message,
      });
      toast.success("Request submitted! Deepa will email you the access code.");
      setRequestSubmitted(true);
    } catch (e) {
      toast.error("Failed to submit request. Please try again.");
    }
    setIsSubmitting(false);
  };

  const handleSubmitApplication = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/career-wheel`, {
        name: applicationData.name,
        email: applicationData.email,
        ratings: ratings,
        priority_areas: priorityAreas,
        desired_changes: changes,
      });
      toast.success("Career wheel results sent! Deepa will review and reach out.");
      setSubmitted(true);
    } catch (e) {
      toast.error("Failed to submit. Please try again.");
    }
    setIsSubmitting(false);
  };

  const averageScore = Math.round(
    Object.values(ratings).reduce((sum, val) => sum + val, 0) / dimensions.length
  );

  // Access Code Screen
  if (step === "access") {
    return (
      <section id="career-wheel" data-testid="career-wheel-section" className="py-20 md:py-32 bg-gradient-to-br from-primary/5 via-accent/5 to-background">
        <div className="max-w-2xl mx-auto px-6 md:px-12">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full mb-6">
              <Sparkles className="h-4 w-4 text-primary" />
              <span className="text-sm font-semibold text-primary uppercase tracking-wide">
                Exclusive Assessment
              </span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-2">The Career Wheel</h2>
            <p className="text-muted-foreground">
              This exclusive assessment requires an access code
            </p>
          </div>

          <Card className="border-2 border-primary/20">
            <CardHeader>
              <CardTitle>Enter Access Code</CardTitle>
              <CardDescription>
                Enter the code Deepa has provided to unlock the Career Wheel assessment
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleVerifyAccess} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="access-code">Access Code *</Label>
                  <Input
                    id="access-code"
                    value={accessCode}
                    onChange={(e) => setAccessCode(e.target.value)}
                    placeholder="Enter your access code"
                    className="text-center font-mono text-lg tracking-widest"
                    required
                    data-testid="access-code-input"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={isVerifying || !accessCode}
                  className="w-full bg-primary text-white hover:bg-primary/90"
                  data-testid="verify-access-btn"
                >
                  {isVerifying ? "Verifying..." : "Unlock Assessment"}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </form>

              <div className="mt-6 pt-6 border-t border-border/50 text-center">
                <p className="text-sm text-muted-foreground mb-3">
                  Don't have an access code?
                </p>
                <Button
                  variant="outline"
                  onClick={() => setStep("request")}
                  data-testid="request-access-btn"
                >
                  Request Access
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
    );
  }

  // Request Access Screen
  if (step === "request") {
    if (requestSubmitted) {
      return (
        <section id="career-wheel" className="py-20 md:py-32 bg-gradient-to-br from-primary/5 via-accent/5 to-background">
          <div className="max-w-2xl mx-auto px-6 text-center">
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Sparkles className="h-8 w-8 text-primary" />
            </div>
            <h2 className="text-3xl font-bold mb-2">Request Submitted!</h2>
            <p className="text-muted-foreground mb-6">
              Thank you! Deepa will review your request and send you the access code via email shortly.
            </p>
            <Button
              onClick={() => {
                setStep("access");
                setRequestSubmitted(false);
              }}
              data-testid="back-to-access-btn"
            >
              Back to Access
            </Button>
          </div>
        </section>
      );
    }

    return (
      <section id="career-wheel" className="py-20 md:py-32 bg-gradient-to-br from-primary/5 via-accent/5 to-background">
        <div className="max-w-2xl mx-auto px-6 md:px-12">
          <div className="text-center mb-8">
            <h2 className="text-3xl md:text-4xl font-bold mb-2">Request Access</h2>
            <p className="text-muted-foreground">
              Fill in your details and Deepa will send you the access code
            </p>
          </div>

          <Card className="border-2 border-primary/20">
            <CardContent className="p-6">
              <form onSubmit={handleRequestAccess} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="request-name">Your Name *</Label>
                  <Input
                    id="request-name"
                    value={requestData.name}
                    onChange={(e) => setRequestData({ ...requestData, name: e.target.value })}
                    placeholder="Your full name"
                    required
                    data-testid="request-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="request-email">Email Address *</Label>
                  <Input
                    id="request-email"
                    type="email"
                    value={requestData.email}
                    onChange={(e) => setRequestData({ ...requestData, email: e.target.value })}
                    placeholder="your@email.com"
                    required
                    data-testid="request-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="request-message">Why do you want to take this assessment?</Label>
                  <Textarea
                    id="request-message"
                    value={requestData.message}
                    onChange={(e) => setRequestData({ ...requestData, message: e.target.value })}
                    rows={3}
                    data-testid="request-message-input"
                  />
                </div>

                <div className="flex gap-3 pt-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setStep("access")}
                    className="flex-1"
                    data-testid="back-to-code-btn"
                  >
                    Back
                  </Button>
                  <Button
                    type="submit"
                    disabled={isSubmitting || !requestData.name || !requestData.email}
                    className="flex-1 bg-primary text-white hover:bg-primary/90"
                    data-testid="submit-request-btn"
                  >
                    {isSubmitting ? "Submitting..." : "Request Access"}
                    <Send className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>
    );
  }

  // Intro Screen
  if (step === "intro") {
    return (
      <section id="career-wheel" data-testid="career-wheel-section" className="py-20 md:py-32 bg-gradient-to-br from-primary/5 via-accent/5 to-background">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full mb-6">
            <Sparkles className="h-4 w-4 text-primary" />
            <span className="text-sm font-semibold text-primary uppercase tracking-wide">
              Free Self-Assessment
            </span>
          </div>

          <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-4">
            The Career Wheel
          </h2>
          <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
            Take this quick self-assessment to visualize your current career satisfaction 
            across 9 key dimensions. Discover where to focus for maximum impact.
          </p>

          <Card className="text-left mb-8 border-2 border-primary/20">
            <CardHeader>
              <CardTitle>What You'll Discover:</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-start gap-2">
                <Sparkles className="h-4 w-4 text-primary mt-1 flex-shrink-0" />
                <p className="text-sm">A visual snapshot of your career satisfaction</p>
              </div>
              <div className="flex items-start gap-2">
                <Sparkles className="h-4 w-4 text-primary mt-1 flex-shrink-0" />
                <p className="text-sm">Priority areas that need your attention</p>
              </div>
              <div className="flex items-start gap-2">
                <Sparkles className="h-4 w-4 text-primary mt-1 flex-shrink-0" />
                <p className="text-sm">Strengths you can leverage</p>
              </div>
              <div className="flex items-start gap-2">
                <Sparkles className="h-4 w-4 text-primary mt-1 flex-shrink-0" />
                <p className="text-sm">Optional: Share results with Deepa for a personalized conversation</p>
              </div>
            </CardContent>
          </Card>

          <p className="text-sm text-muted-foreground mb-4">
            Takes about 5 minutes • Completely confidential
          </p>

          <Button
            onClick={() => setStep("survey")}
            size="lg"
            className="bg-primary text-white hover:bg-primary/90"
            data-testid="start-career-wheel-btn"
          >
            Start Assessment
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </section>
    );
  }

  // Survey Screen
  if (step === "survey") {
    return (
      <section id="career-wheel" data-testid="career-wheel-section" className="py-20 md:py-32 bg-gradient-to-br from-primary/5 via-accent/5 to-background">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <div className="text-center mb-8">
            <h2 className="text-3xl md:text-4xl font-bold mb-2">Rate Each Dimension</h2>
            <p className="text-muted-foreground">
              Slide from 0 (not satisfied) to 100 (completely satisfied)
            </p>
          </div>

          <Card className="border-2 border-primary/20">
            <CardContent className="p-6 md:p-8 space-y-6">
              {dimensions.map((d) => (
                <div key={d.key} className="space-y-3" data-testid={`dimension-${d.key}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-base font-semibold">{d.label}</Label>
                      <p className="text-xs text-muted-foreground mt-1">{d.description}</p>
                    </div>
                    <span className="text-2xl font-bold text-primary min-w-[3rem] text-right">
                      {ratings[d.key]}
                    </span>
                  </div>
                  <Slider
                    value={[ratings[d.key]]}
                    onValueChange={(val) => handleRatingChange(d.key, val)}
                    max={100}
                    step={5}
                    className="w-full"
                    data-testid={`slider-${d.key}`}
                  />
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>0 - Very Low</span>
                    <span>50 - Moderate</span>
                    <span>100 - Excellent</span>
                  </div>
                </div>
              ))}

              <div className="flex justify-between pt-6 border-t border-border/50">
                <Button
                  variant="outline"
                  onClick={() => setStep("intro")}
                  data-testid="back-to-intro-btn"
                >
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back
                </Button>
                <Button
                  onClick={() => setStep("results")}
                  className="bg-primary text-white hover:bg-primary/90"
                  data-testid="view-results-btn"
                >
                  View My Career Wheel
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
    );
  }

  // Results Screen with Visualization
  if (step === "results") {
    return (
      <section id="career-wheel" data-testid="career-wheel-section" className="py-20 md:py-32 bg-gradient-to-br from-primary/5 via-accent/5 to-background">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <div className="text-center mb-8">
            <h2 className="text-3xl md:text-4xl font-bold mb-2">Your Career Wheel</h2>
            <p className="text-muted-foreground mb-4">
              Overall Career Satisfaction: <strong className="text-primary text-2xl">{averageScore}/100</strong>
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 items-center">
            {/* Radar Chart */}
            <Card className="border-2 border-primary/20">
              <CardContent className="p-6">
                <div className="w-full" style={{ height: "400px" }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <RadarChart data={chartData} margin={{ top: 20, right: 30, bottom: 20, left: 30 }}>
                      <PolarGrid stroke="#4A6741" strokeOpacity={0.3} />
                      <PolarAngleAxis 
                        dataKey="dimension" 
                        tick={{ fill: '#333', fontSize: 11 }}
                      />
                      <PolarRadiusAxis 
                        angle={90} 
                        domain={[0, 100]} 
                        tick={{ fill: '#666', fontSize: 10 }}
                      />
                      <Radar
                        name="Career Satisfaction"
                        dataKey="value"
                        stroke="#4A6741"
                        fill="#4A6741"
                        fillOpacity={0.4}
                        strokeWidth={2}
                      />
                    </RadarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Scores Breakdown */}
            <Card className="border-2 border-primary/20">
              <CardHeader>
                <CardTitle>Dimension Scores</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {dimensions.map((d) => {
                  const score = ratings[d.key];
                  const color = score < 40 ? "bg-red-500" : score < 70 ? "bg-yellow-500" : "bg-green-500";
                  return (
                    <div key={d.key} className="space-y-1">
                      <div className="flex justify-between text-sm">
                        <span className="font-medium">{d.label}</span>
                        <span className="font-bold">{score}</span>
                      </div>
                      <div className="w-full bg-muted rounded-full h-2 overflow-hidden">
                        <div 
                          className={`h-full ${color} transition-all duration-500`}
                          style={{ width: `${score}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          </div>

          {/* Reflection Questions */}
          <Card className="mt-8 border-2 border-primary/20">
            <CardHeader>
              <CardTitle>Reflect on Your Results</CardTitle>
              <CardDescription>
                Take a moment to think about what your wheel is telling you
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Your 2-3 priority areas for immediate focus:</Label>
                <Textarea
                  value={priorityAreas}
                  onChange={(e) => setPriorityAreas(e.target.value)}
                  placeholder="e.g., Work/Life Balance, Career Progression..."
                  rows={2}
                  data-testid="priority-areas-input"
                  className="mt-2"
                />
              </div>
              <div>
                <Label>What specifically do you want to change to increase satisfaction?</Label>
                <Textarea
                  value={changes}
                  onChange={(e) => setChanges(e.target.value)}
                  placeholder="Describe the specific changes you'd like to make..."
                  rows={3}
                  data-testid="changes-input"
                  className="mt-2"
                />
              </div>
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="mt-8 flex flex-col md:flex-row gap-4 justify-center">
            <Button
              variant="outline"
              onClick={() => setStep("survey")}
              data-testid="edit-ratings-btn"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Edit Ratings
            </Button>
            <Button
              variant="outline"
              onClick={resetSurvey}
              data-testid="start-over-btn"
            >
              <RotateCcw className="mr-2 h-4 w-4" />
              Start Over
            </Button>
            <Button
              onClick={() => setStep("application")}
              className="bg-primary text-white hover:bg-primary/90"
              data-testid="send-to-deepa-btn"
            >
              Share Results with Deepa
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      </section>
    );
  }

  // Application Screen
  if (step === "application") {
    if (submitted) {
      return (
        <section id="career-wheel" className="py-20 md:py-32 bg-gradient-to-br from-primary/5 via-accent/5 to-background">
          <div className="max-w-2xl mx-auto px-6 text-center">
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Sparkles className="h-8 w-8 text-primary" />
            </div>
            <h2 className="text-3xl font-bold mb-2">Results Sent!</h2>
            <p className="text-muted-foreground mb-6">
              Thank you! Deepa has received your Career Wheel results and will reach out to schedule a conversation.
            </p>
            <Button onClick={resetSurvey} data-testid="take-again-btn">
              Take Assessment Again
            </Button>
          </div>
        </section>
      );
    }

    return (
      <section id="career-wheel" className="py-20 md:py-32 bg-gradient-to-br from-primary/5 via-accent/5 to-background">
        <div className="max-w-2xl mx-auto px-6 md:px-12">
          <Card className="border-2 border-primary/20">
            <CardHeader>
              <CardTitle>Share Your Results</CardTitle>
              <CardDescription>
                Send your Career Wheel to Deepa for a personalized coaching conversation
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmitApplication} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="wheel-name">Your Name *</Label>
                  <Input
                    id="wheel-name"
                    value={applicationData.name}
                    onChange={(e) => setApplicationData({ ...applicationData, name: e.target.value })}
                    placeholder="Your full name"
                    required
                    data-testid="wheel-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="wheel-email">Email Address *</Label>
                  <Input
                    id="wheel-email"
                    type="email"
                    value={applicationData.email}
                    onChange={(e) => setApplicationData({ ...applicationData, email: e.target.value })}
                    placeholder="your@email.com"
                    required
                    data-testid="wheel-email-input"
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setStep("results")}
                    className="flex-1"
                    data-testid="back-to-results-btn"
                  >
                    Back
                  </Button>
                  <Button
                    type="submit"
                    disabled={isSubmitting || !applicationData.name || !applicationData.email}
                    className="flex-1 bg-primary text-white hover:bg-primary/90"
                    data-testid="submit-wheel-btn"
                  >
                    {isSubmitting ? "Sending..." : "Send Results"}
                    <Send className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>
    );
  }

  return null;
};

export default CareerWheel;
